import {
  AuthenticationError,
  HttpError,
  NetworkError,
  RequestCancelledError,
  ResponseParseError,
  TimeoutError,
} from './apiErrors';

export interface HttpResponseInterface<T> extends Response {
  parsedBody?: T;
}

export interface HttpClientDependencies {
  auth: {
    readonly authenticated: boolean;
    readonly token?: string | null;
    updateToken: (minValidity: number) => Promise<unknown>;
    login: () => Promise<void>;
  };
  fetchRequest?: typeof fetch;
  onConnectionStatus?: (online: boolean) => void;
  refreshTokenMinValidity: number;
  refreshBeforeRequestByDefault?: boolean;
}

export interface HttpRequestConfig extends RequestInit {
  requireAuthentication?: boolean;
  refreshBeforeRequest?: boolean;
}

type ClientOptions = {
  retryOnUnauthorized?: boolean;
  responseMode?: 'json' | 'raw';
};
const BASE_URL = '/api';
const OFFLINE_HTTP_STATUSES = new Set([502, 503]);

// DOMException may come from a different realm and need not extend Error.
const isNamedError = (
  error: unknown,
  name: string,
): error is { name: string; message: string } =>
  typeof error === 'object' &&
  error !== null &&
  'name' in error &&
  error.name === name &&
  'message' in error &&
  typeof error.message === 'string';

// Each client owns its refresh coordination; transport has no Redux dependency.
export function createHttpClient({
  auth,
  fetchRequest = (request, config) => fetch(request, config),
  onConnectionStatus,
  refreshTokenMinValidity,
  refreshBeforeRequestByDefault = false,
}: HttpClientDependencies) {
  const requestLogin = () => {
    void auth.login().catch(() => undefined);
  };
  const throwIfCancelled = (signal?: AbortSignal | null) => {
    if (!signal?.aborted) return;
    const reason: unknown = signal.reason;
    if (isNamedError(reason, 'TimeoutError')) {
      throw new TimeoutError(reason.message, reason);
    }
    throw new RequestCancelledError('Request cancelled', reason);
  };
  const waitForRefresh = (
    promise: Promise<void>,
    signal?: AbortSignal | null,
  ) => {
    throwIfCancelled(signal);
    if (!signal) return promise;
    return new Promise<void>((resolve, reject) => {
      const onAbort = () => {
        try {
          throwIfCancelled(signal);
        } catch (error) {
          reject(error);
        }
      };
      signal.addEventListener('abort', onAbort, { once: true });
      void promise.then(resolve, reject).finally(() => {
        signal.removeEventListener('abort', onAbort);
      });
    });
  };
  let tokenRefreshPromise: Promise<void> | undefined;
  let tokenRefreshForced = false;

  const trackTokenRefresh = (promise: Promise<void>, forced: boolean) => {
    const tracked = promise.finally(() => {
      if (tokenRefreshPromise === tracked) {
        tokenRefreshPromise = undefined;
      }
    });
    tokenRefreshPromise = tracked;
    tokenRefreshForced = forced;
    void tracked.catch(() => undefined);
    return tracked;
  };

  function beginTokenRefresh(force = false): Promise<void> | undefined {
    if (!auth.authenticated) {
      return undefined;
    }

    if (tokenRefreshPromise && (tokenRefreshForced || !force)) {
      return tokenRefreshPromise;
    }

    // A conditional refresh in flight may keep the token the server just
    // rejected, so a forced refresh follows it instead of joining it.
    const refreshPromise = (
      tokenRefreshPromise
        ? tokenRefreshPromise.then(() => auth.updateToken(-1))
        : auth.updateToken(force ? -1 : refreshTokenMinValidity)
    ).then(() => undefined);

    return trackTokenRefresh(refreshPromise, force);
  }

  const awaitActiveTokenRefresh = async () => {
    if (!tokenRefreshPromise) {
      return;
    }

    await tokenRefreshPromise;
  };

  async function get<T>(
    path: string,
    config: HttpRequestConfig = { method: 'get' },
    baseUrl?: string,
  ): Promise<HttpResponseInterface<T>> {
    return await client<T>(path, config, baseUrl);
  }

  async function remove<T>(
    path: string,
    body?: string,
    config: HttpRequestConfig = {},
    baseUrl?: string,
  ): Promise<HttpResponseInterface<T>> {
    return await client<T>(
      path,
      { method: 'delete', body, ...config },
      baseUrl,
    );
  }

  async function put<T>(
    path: string,
    body?: string,
    config: HttpRequestConfig = {},
    baseUrl?: string,
  ): Promise<HttpResponseInterface<T>> {
    return await client<T>(path, { method: 'put', body, ...config }, baseUrl);
  }

  async function post<T>(
    path: string,
    body?: string,
    config: HttpRequestConfig = {},
    baseUrl?: string,
  ): Promise<HttpResponseInterface<T>> {
    return await client<T>(path, { method: 'post', body, ...config }, baseUrl);
  }

  async function client<T>(
    path: string,
    config: HttpRequestConfig,
    baseUrl: string = BASE_URL,
    options: ClientOptions = {},
  ): Promise<HttpResponseInterface<T>> {
    const { retryOnUnauthorized = true, responseMode = 'json' } = options;
    const { requireAuthentication, refreshBeforeRequest, ...fetchConfig } =
      config;

    throwIfCancelled(config.signal);
    try {
      await waitForRefresh(
        (refreshBeforeRequest ?? refreshBeforeRequestByDefault) &&
          retryOnUnauthorized
          ? (beginTokenRefresh() ?? Promise.resolve())
          : awaitActiveTokenRefresh(),
        config.signal,
      );
    } catch (error) {
      if (
        error instanceof RequestCancelledError ||
        error instanceof TimeoutError
      )
        throw error;
      throw new AuthenticationError('Token refresh failed', error);
    }
    throwIfCancelled(config.signal);

    if (
      (requireAuthentication || refreshBeforeRequestByDefault) &&
      !auth.token
    ) {
      requestLogin();
      throw new AuthenticationError('Authentication required');
    }
    const headersInit = config.headers;
    const headers = new Headers(headersInit);

    if (auth.token) {
      headers.set('Authorization', `Bearer ${auth.token}`);
    } else if (headers.has('Authorization')) {
      headers.delete('Authorization');
    }

    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    headers.set('sandvik-user-agent', 'PlantDesignerWeb/1.0.0');

    const requestConfig: RequestInit = {
      ...fetchConfig,
      headers,
    };

    const request = new Request(`${baseUrl}/${path}`, requestConfig);

    let response: HttpResponseInterface<T>;

    try {
      response = await fetchRequest(request);
    } catch (error) {
      throwIfCancelled(config.signal);
      if (isNamedError(error, 'AbortError')) {
        throw new RequestCancelledError(error.message, error);
      }
      onConnectionStatus?.(false);
      if (isNamedError(error, 'TimeoutError')) {
        throw new TimeoutError(error.message, error);
      }
      throw new NetworkError(
        error instanceof Error ? error.message : 'Network request failed',
        error,
      );
    }

    if (OFFLINE_HTTP_STATUSES.has(response.status)) {
      const service = path.substring(0, path.indexOf('/'));
      const message = `Failed to connect to ${service} server`;
      onConnectionStatus?.(false);
      throw new TimeoutError(message);
    }

    if (response.status === 401) {
      if (!retryOnUnauthorized) {
        requestLogin();
        throw new AuthenticationError(response.statusText || 'Unauthorized');
      }

      try {
        const refreshPromise = beginTokenRefresh(true);
        if (!refreshPromise) {
          requestLogin();
          throw new AuthenticationError('Authentication unavailable');
        }

        await waitForRefresh(refreshPromise, config.signal);
      } catch (error) {
        if (
          error instanceof RequestCancelledError ||
          error instanceof TimeoutError
        )
          throw error;
        if (!(error instanceof AuthenticationError)) requestLogin();
        throw new AuthenticationError(
          error instanceof Error ? error.message : 'Unauthorized',
          error,
        );
      }
      // A retried request can fail independently of authentication.
      return client<T>(path, config, baseUrl, {
        retryOnUnauthorized: false,
        responseMode,
      });
    }

    onConnectionStatus?.(true);

    if (!response.ok) {
      throw new HttpError(response.status, response.statusText);
    }

    if (responseMode === 'raw') return response;

    let text: string;
    try {
      text = await response.text();
    } catch (error) {
      throwIfCancelled(config.signal);
      if (isNamedError(error, 'AbortError')) {
        throw new RequestCancelledError(error.message, error);
      }
      onConnectionStatus?.(false);
      throw new NetworkError('Failed to read response body', error);
    }
    throwIfCancelled(config.signal);
    if (text.trim()) {
      try {
        response.parsedBody = JSON.parse(text) ?? response.parsedBody;
      } catch (error) {
        const contentType = response.headers.get('Content-Type') ?? '';
        if (/\bjson\b/i.test(contentType)) {
          throw new ResponseParseError('Invalid JSON response', error);
        }
        // Some successful endpoints return non-JSON bodies.
      }
    }
    return response;
  }

  async function requestRaw(
    path: string,
    config: HttpRequestConfig = {},
    baseUrl?: string,
  ): Promise<Response> {
    return client(path, config, baseUrl, { responseMode: 'raw' });
  }

  async function getBlob(
    path: string,
    config: HttpRequestConfig = {},
    baseUrl?: string,
  ): Promise<Blob> {
    const response = await requestRaw(
      path,
      { method: 'get', ...config },
      baseUrl,
    );
    try {
      const blob = await response.blob();
      throwIfCancelled(config.signal);
      return blob;
    } catch (error) {
      throwIfCancelled(config.signal);
      if (isNamedError(error, 'AbortError'))
        throw new RequestCancelledError(error.message, error);
      onConnectionStatus?.(false);
      if (isNamedError(error, 'TimeoutError'))
        throw new TimeoutError(error.message, error);
      throw new NetworkError('Failed to read response body', error);
    }
  }

  return { get, post, put, remove, beginTokenRefresh, requestRaw, getBlob };
}
